import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { IPrivateInsuranceProps } from "./models";
import {
  createTicketAutomationReceivedMessagePrivateInsurance,
  getTicketAutomationReceivedMessagePrivateInsuranceById,
  privateInsuranceRegisterList,
  updateTicketAutomationReceivedMessagePrivateInsuranceById,
} from "../../../../../../../services/ticketAutomationReceivedMessagePrivateInsurance.service";
import {
  privateInsuranceSchema,
  TPrivateInsuranceForm,
} from "./privateInsuranceSchema";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedMessagePrivateInsurance.";

const PrivateInsurance = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
}: IPrivateInsuranceProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedMessagePrivateInsurance,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedMessagePrivateInsuranceById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedMessagePrivateInsuranceById,
  );

  const updateHandler = (v: TPrivateInsuranceForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        externalId: v.external.id,
        externalName: v.external.name,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TPrivateInsuranceForm) =>
    save({
      ...v,
      externalId: v.external.id,
      externalName: v.external.name,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TPrivateInsuranceForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TPrivateInsuranceForm = {
          ...data,
          external: {
            id: data.externalId,
            name: data.externalName,
          },
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const [
    optionsTicketAutomationReceivedSlotCalendars,
    setTicketAutomationReceivedSlotCalendars,
  ] = useState<ISelectType[]>([]);

  const { sendRequest: privateInsuranceRequest } = useFetch(
    privateInsuranceRegisterList,
  );

  const insuranceRequestParams = useMemo(() => {
    return {
      EnableCode: true,
      TypeCode: 1,
      ShowAppAndOnlineCalendar: true,
    };
  }, []);

  const fetchTicketAutomationReceivedSlotCalendars = useCallback(async () => {
    const { data, success } = await privateInsuranceRequest(
      insuranceRequestParams,
    );
    if (data && success) {
      setTicketAutomationReceivedSlotCalendars(
        data.map((d) => ({
          id: d.identifier.toString(),
          name: d.insuranceName,
        })),
      );
    }
  }, [privateInsuranceRequest, insuranceRequestParams]);

  useEffect(() => {
    fetchTicketAutomationReceivedSlotCalendars();
  }, [fetchTicketAutomationReceivedSlotCalendars]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TPrivateInsuranceForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "private_insurance")}
      schema={privateInsuranceSchema}
      subtitle={t(translationPath + "new_private_insurance")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              fullWidth
              options={optionsTicketAutomationReceivedSlotCalendars}
              name="external"
              label={t(translationPath + "private_insurance")}
              getOptionLabel={(option) => option.name}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default PrivateInsurance;
